/**
 * @zendir/ui - HeaderIconWithStatus Component
 *
 * Shared component for card/chart header icons with status indicator.
 * Follows MonitoringIcon pattern for Astro UX compliance.
 *
 * Features:
 * - Status indicator positioned at top-left with transform offset
 * - Glow effect in dark mode for visibility
 * - Border in light mode for WCAG AA compliance
 * - Theme-aware colors
 * - Consistent sizing across all usages
 */

import React, { memo } from "react";
import { useTheme } from "../../theme";
import { type StatusLevel } from "../../utils";
import { AstroIcon, type AstroIconName } from "./AstroIcon";

// =============================================================================
// Astro UX Status Border Colors - Required for light theme WCAG compliance
// https://www.astrouxds.com/patterns/status-system/
// =============================================================================

const STATUS_BORDER_COLORS: Record<StatusLevel, string> = {
  off: "#3c3e42",
  standby: "#285766",
  normal: "#005a00",
  caution: "#645600",
  serious: "#664618",
  critical: "#661102",
};

// =============================================================================
// Status Shape SVG - Matches MonitoringIcon exactly
// =============================================================================

interface StatusShapeProps {
  status: StatusLevel;
  size: number;
  color: string;
  borderColor?: string;
  glow?: boolean;
}

const StatusShape = ({
  status,
  size,
  color,
  borderColor,
  glow = true,
}: StatusShapeProps) => {
  const hasBorder = !!borderColor;
  const strokeWidth = hasBorder ? 1 : 0;
  const glowFilter =
    glow && !hasBorder ? `drop-shadow(0 0 3px ${color})` : undefined;

  const renderShape = () => {
    switch (status) {
      case "off":
        return (
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 2.5 : 3}
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "standby":
        return (
          <circle
            cx="6"
            cy="6"
            r="3.5"
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
        );
      case "normal":
        return (
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 4.5 : 5}
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "caution":
        return (
          <rect
            x={hasBorder ? 1.5 : 1}
            y={hasBorder ? 1.5 : 1}
            width={hasBorder ? 9 : 10}
            height={hasBorder ? 9 : 10}
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "serious":
        return (
          <polygon
            points="6,1 11,6 6,11 1,6"
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "critical":
        return (
          <polygon
            points="6,11 1,2 11,2"
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      default:
        return null;
    }
  };

  return (
    <svg
      viewBox="0 0 12 12"
      width={size}
      height={size}
      style={{ filter: glowFilter }}
    >
      {renderShape()}
    </svg>
  );
};

// =============================================================================
// HeaderIconWithStatus Component
// =============================================================================

export interface HeaderIconWithStatusProps {
  /** Astro icon name */
  icon: AstroIconName | string;
  /** Icon size in pixels */
  size?: number;
  /** Status level */
  status: StatusLevel;
  /** Custom status color (optional - defaults to theme status color) */
  statusColor?: string;
  /** Override light theme detection */
  isLightTheme?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
}

/**
 * HeaderIconWithStatus - Card/Chart header icon with status indicator
 *
 * Follows MonitoringIcon pattern exactly:
 * - Icon colored by status
 * - Status symbol at top-left with transform offset
 * - Glow in dark mode, border in light mode
 *
 * @example
 * ```tsx
 * <HeaderIconWithStatus
 *   icon="satellite-transmit"
 *   status="normal"
 *   size={24}
 * />
 * ```
 */
export const HeaderIconWithStatus = memo(function HeaderIconWithStatus({
  icon,
  size = 24,
  status,
  statusColor: statusColorProp,
  isLightTheme: isLightThemeProp,
  className,
  style,
}: HeaderIconWithStatusProps) {
  const { tokens, mode } = useTheme();

  // Theme detection
  const isLightTheme = isLightThemeProp ?? mode === "light";

  // Status color from theme or prop
  const statusColor =
    statusColorProp ?? tokens.colors.status[status] ?? "#a4abb6";

  // Border color for light theme (WCAG compliance)
  const borderColor = isLightTheme ? STATUS_BORDER_COLORS[status] : undefined;

  // ═══════════════════════════════════════════════════════════════════════════
  // SIZING CONTROLS - Adjust these to match MonitoringIcon proportions
  // ═══════════════════════════════════════════════════════════════════════════
  // Badge size: ~35% of icon size (same ratio as MonitoringIcon's 9px badge on 24px icon)
  const badgeSize = Math.max(7, Math.min(12, Math.round(size * 0.38)));

  // Transform offset: translate(-35%, -35%) matches MonitoringIcon exactly
  const transformOffset = "translate(-35%, -35%)";

  return (
    <div
      role="img"
      aria-label={`${icon} — status: ${status}`}
      className={className}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        flexShrink: 0,
        color: statusColor,
        ...style,
      }}
    >
      {/* Icon - colored by status */}
      <AstroIcon
        name={icon as AstroIconName}
        size={size}
        color={statusColor}
        aria-hidden="true"
      />

      {/* Status symbol - top-left corner with transform offset */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          transform: transformOffset,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <StatusShape
          status={status}
          size={badgeSize}
          color={statusColor}
          borderColor={borderColor}
          glow={!isLightTheme}
        />
      </span>
    </div>
  );
});

export default HeaderIconWithStatus;
